import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProductComparisonPanel = ({ 
  product, 
  title, 
  isPrimary = false,
  className = '' 
}) => {
  if (!product) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="text-center text-muted-foreground">
          <Icon name="Package" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No product selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        {isPrimary && (
          <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded">
            Primary
          </span>
        )}
      </div>
      {/* Product Image */}
      {product?.image && (
        <div className="mb-4">
          <div className="w-full h-32 bg-muted rounded-lg overflow-hidden">
            <Image
              src={product?.image}
              alt={product?.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      {/* Product Details */}
      <div className="space-y-4">
        {/* Product Name */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Product Name
          </label>
          <p className="text-sm font-medium text-foreground mt-1">
            {product?.name}
          </p>
        </div>

        {/* Brand */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Brand
          </label>
          <p className="text-sm text-foreground mt-1">
            {product?.brand}
          </p>
        </div>

        {/* MPN */}
        {product?.mpn && (
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              MPN
            </label>
            <p className="text-sm font-mono text-foreground mt-1">
              {product?.mpn}
            </p>
          </div>
        )}

        {/* EAN */}
        {product?.ean && (
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              EAN
            </label>
            <p className="text-sm font-mono text-foreground mt-1">
              {product?.ean}
            </p>
          </div>
        )}

        {/* SKU */}
        {product?.sku && (
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              SKU
            </label>
            <p className="text-sm font-mono text-foreground mt-1">
              {product?.sku}
            </p>
          </div>
        )}

        {/* Description */}
        {product?.description && (
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Description
            </label>
            <p className="text-sm text-foreground mt-1 leading-relaxed">
              {product?.description}
            </p>
          </div>
        )}

        {/* Price */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Price
          </label>
          <p className="text-lg font-semibold text-foreground mt-1">
            ${product?.price?.toFixed(2)}
          </p>
        </div>

        {/* Stock Status */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Stock Status
          </label>
          <div className="flex items-center space-x-2 mt-1">
            <div className={`w-2 h-2 rounded-full ${
              product?.inStock ? 'bg-success' : 'bg-error'
            }`} />
            <span className={`text-sm font-medium ${
              product?.inStock ? 'text-success' : 'text-error'
            }`}>
              {product?.inStock ? 'In Stock' : 'Out of Stock'}
            </span>
            {product?.quantity && (
              <span className="text-sm text-muted-foreground">
                ({product?.quantity} units)
              </span>
            )}
          </div>
        </div>

        {/* Supplier Information */}
        <div className="pt-4 border-t border-border">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Supplier
          </label>
          <div className="mt-2 space-y-1">
            <p className="text-sm font-medium text-foreground">
              {product?.supplier?.name}
            </p>
            {product?.supplier?.contact && (
              <p className="text-xs text-muted-foreground">
                {product?.supplier?.contact}
              </p>
            )}
            {product?.supplier?.lastUpdated && (
              <p className="text-xs text-muted-foreground">
                Updated: {new Date(product.supplier.lastUpdated)?.toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductComparisonPanel;